import { process } from '@progress/kendo-data-query';
import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  CanCopyFavoriteFolders,
  CanShareFavoriteFolders,
  IsBuyer,
} from 'src/constants/permissionsList';
import {
  SHOPPING_LIST_FILTER_OPTIONS,
  SHOPPING_LIST_SORT_OPTIONS,
} from 'src/contexts/viewShoppingListsConstants';
import useAuth from 'src/hooks/useAuth';
import useDebounce from 'src/hooks/useDebounce';
import usePermission from 'src/hooks/usePermissions';
import { fetchShoppingListsForExport } from 'src/services/favorite/fetchShoppingListsForExport';
import { mapFavoriteFoldersToShoppingLists } from 'src/services/favorite/mapFavoriteFolderToShoppingList';
import useCopyShoppingListMutation from 'src/services/favorite/useCopyShoppingListMutation';
import useDeleteShoppingListMutation from 'src/services/favorite/useDeleteShoppingListMutation';
import useGetAllFavoriteFoldersQuery from 'src/services/favorite/useGetAllFavoriteFoldersQuery';
import useShareShoppingListMutation from 'src/services/favorite/useShareShoppingListMutation';
import useUpsertShoppingListMutation from 'src/services/favorite/useUpsertShoppingListMutation';
import useGlCodesApi, { GLCodeDropdownData } from 'src/services/useGlCodesApi';
import {
  CopyShoppingListRequest,
  CopyShoppingListResponse,
  ShareShoppingListRequest,
  ShareShoppingListResponse,
  UpsertShoppingListRequest,
  UpsertShoppingListResponse,
} from 'src/types/favorite';
import { SetState } from 'src/types/generic';
import {
  ShoppingList,
  ShoppingListFilterOption,
  ShoppingListSortOption,
} from 'src/types/view_shopping_lists/ShoppingList';
import { exportShoppingListsToExcel } from 'src/utils/exportShoppingListsToExcel';
import { createContext } from 'use-context-selector';
import {
  buildShareShoppingListToastMessages,
  buildShoppingListsDataState,
} from './ViewShoppingListsUtils';

const toFolderIds = (ids: string[]): number[] =>
  Array.from(
    new Set(
      ids.map((id) => Number(id)).filter((folderId) => Number.isFinite(folderId) && folderId > 0)
    )
  );

export interface ViewShoppingListsContextShape {
  shoppingLists: ShoppingList[];
  isLoading: boolean;
  searchValue: string;
  setSearchValue: (value: string) => void;
  selectedFilter: ShoppingListFilterOption;
  handleSelectedFilterChange: (filter: ShoppingListFilterOption) => void;
  selectedSort: ShoppingListSortOption;
  setSelectedSort: SetState<ShoppingListSortOption>;
  hasActiveFilters: boolean;
  clearFilters: () => void;
  selectedShoppingListIds: string[];
  hasSelectedShoppingLists: boolean;
  canExportSelectedShoppingLists: boolean;
  toggleShoppingListSelection: (id: string) => void;
  toggleAllShoppingListSelection: () => void;
  exportSelectedShoppingLists: () => Promise<void>;
  isExportingShoppingLists: boolean;
  canShareShoppingLists: boolean;
  canCopyShoppingLists: boolean;
  glCodeOptions: GLCodeDropdownData;
  areGLCodesLoading: boolean;
  isCreateShoppingListPanelOpen: boolean;
  openCreateShoppingListPanel: () => void;
  closeCreateShoppingListPanel: () => void;
  editingShoppingList: ShoppingList | null;
  isEditShoppingListPanelOpen: boolean;
  openEditShoppingListPanel: (shoppingList: ShoppingList) => void;
  closeEditShoppingListPanel: () => void;
  upsertShoppingList: (request: UpsertShoppingListRequest) => Promise<UpsertShoppingListResponse>;
  deleteShoppingList: (folderId: number) => Promise<void>;
  deleteSelectedShoppingLists: () => Promise<void>;
  isUpsertingShoppingList: boolean;
  isDeletingShoppingList: boolean;
  deletingShoppingListId: string | null;
  sharingShoppingList: ShoppingList | null;
  shareFolderIds: number[];
  isShareShoppingListPanelOpen: boolean;
  openShareShoppingListPanel: (shoppingList?: ShoppingList | null) => void;
  closeShareShoppingListPanel: () => void;
  shareShoppingList: (request: ShareShoppingListRequest) => Promise<ShareShoppingListResponse>;
  isSharingShoppingList: boolean;
  copyingShoppingList: ShoppingList | null;
  copyFolderIds: number[];
  isCopyShoppingListPanelOpen: boolean;
  openCopyShoppingListPanel: (shoppingList?: ShoppingList | null) => void;
  closeCopyShoppingListPanel: () => void;
  copyShoppingList: (request: CopyShoppingListRequest) => Promise<CopyShoppingListResponse>;
  isCopyingShoppingList: boolean;
}

export const ViewShoppingListsContext = createContext<ViewShoppingListsContextShape>(
  {} as ViewShoppingListsContextShape
);

export const ViewShoppingListsContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const { clientId, companyId } = useAuth();
  const isBuyer = usePermission({ permissions: IsBuyer });
  const hasShareFavoriteFoldersPermission = usePermission({
    permissions: CanShareFavoriteFolders,
  });
  const hasCopyFavoriteFoldersPermission = usePermission({
    permissions: CanCopyFavoriteFolders,
  });
  const canShareShoppingLists = isBuyer && hasShareFavoriteFoldersPermission;
  const canCopyShoppingLists = isBuyer && hasCopyFavoriteFoldersPermission;
  const { data: favoriteFolders = [], isLoading, isError } = useGetAllFavoriteFoldersQuery();
  const { glCodeOptions, areGLCodesLoading } = useGlCodesApi(
    companyId ?? '',
    undefined,
    undefined,
    true
  );
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue.trim(), 500);
  const [selectedFilter, setSelectedFilter] = useState<ShoppingListFilterOption>(
    SHOPPING_LIST_FILTER_OPTIONS[0]
  );
  const [selectedSort, setSelectedSort] = useState<ShoppingListSortOption>(
    SHOPPING_LIST_SORT_OPTIONS[0]
  );
  const [isCreateShoppingListPanelOpen, setIsCreateShoppingListPanelOpen] = useState(false);
  const [editingShoppingList, setEditingShoppingList] = useState<ShoppingList | null>(null);
  const [isEditShoppingListPanelOpen, setIsEditShoppingListPanelOpen] = useState(false);
  const [sharingShoppingList, setSharingShoppingList] = useState<ShoppingList | null>(null);
  const [shareFolderIds, setShareFolderIds] = useState<number[]>([]);
  const [isShareShoppingListPanelOpen, setIsShareShoppingListPanelOpen] = useState(false);
  const [copyingShoppingList, setCopyingShoppingList] = useState<ShoppingList | null>(null);
  const [copyFolderIds, setCopyFolderIds] = useState<number[]>([]);
  const [isCopyShoppingListPanelOpen, setIsCopyShoppingListPanelOpen] = useState(false);
  const [selectedShoppingListIds, setSelectedShoppingListIds] = useState<string[]>([]);
  const [deletingShoppingListId, setDeletingShoppingListId] = useState<string | null>(null);
  const [isExportingShoppingLists, setIsExportingShoppingLists] = useState(false);

  const toggleShoppingListSelection = useCallback((id: string) => {
    setSelectedShoppingListIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  }, []);

  const upsertShoppingListMutation = useUpsertShoppingListMutation();
  const deleteShoppingListMutation = useDeleteShoppingListMutation();
  const shareShoppingListMutation = useShareShoppingListMutation();
  const copyShoppingListMutation = useCopyShoppingListMutation();
  const { mutateAsync: upsertShoppingListAsync, isPending: isUpsertingShoppingList } =
    upsertShoppingListMutation;
  const { mutateAsync: deleteShoppingListAsync, isPending: isDeletingShoppingList } =
    deleteShoppingListMutation;
  const { mutateAsync: shareShoppingListAsync, isPending: isSharingShoppingList } =
    shareShoppingListMutation;
  const { mutateAsync: copyShoppingListAsync, isPending: isCopyingShoppingList } =
    copyShoppingListMutation;

  const openCreateShoppingListPanel = useCallback(() => {
    setIsCreateShoppingListPanelOpen(true);
  }, []);

  const closeCreateShoppingListPanel = useCallback(() => {
    setIsCreateShoppingListPanelOpen(false);
  }, []);

  const openEditShoppingListPanel = useCallback((shoppingList: ShoppingList) => {
    setEditingShoppingList(shoppingList);
    setIsEditShoppingListPanelOpen(true);
  }, []);

  const closeEditShoppingListPanel = useCallback(() => {
    setIsEditShoppingListPanelOpen(false);
    setEditingShoppingList(null);
  }, []);

  const upsertShoppingList = useCallback(
    async (request: UpsertShoppingListRequest) => {
      const isCreate = request.favoriteCategoryId == null;
      const response = await upsertShoppingListAsync(request);
      toast.success(
        isCreate ? t('Shopping list created successfully') : t('Shopping list updated successfully')
      );
      return response;
    },
    [t, upsertShoppingListAsync]
  );

  const deleteShoppingList = useCallback(
    async (folderId: number) => {
      setDeletingShoppingListId(String(folderId));
      try {
        await deleteShoppingListAsync(folderId);
        toast.success(t('Shopping list deleted successfully'));
      } finally {
        setDeletingShoppingListId(null);
      }
    },
    [deleteShoppingListAsync, t]
  );

  const deleteSelectedShoppingLists = useCallback(async () => {
    const ownedSelectedIds = mapFavoriteFoldersToShoppingLists(favoriteFolders)
      .filter((list) => selectedShoppingListIds.includes(list.id) && !list.isShare)
      .map((list) => list.id);
    const folderIds = toFolderIds(ownedSelectedIds);
    if (folderIds.length === 0) {
      return;
    }

    setDeletingShoppingListId(String(folderIds[0]));
    try {
      for (const folderId of folderIds) {
        setDeletingShoppingListId(String(folderId));
        await deleteShoppingListAsync(folderId);
      }
      toast.success(t('Shopping list deleted successfully'));
      setSelectedShoppingListIds([]);
    } finally {
      setDeletingShoppingListId(null);
    }
  }, [deleteShoppingListAsync, favoriteFolders, selectedShoppingListIds, t]);

  const closeShareShoppingListPanel = useCallback(() => {
    setIsShareShoppingListPanelOpen(false);
    setSharingShoppingList(null);
    setShareFolderIds([]);
  }, []);

  const closeCopyShoppingListPanel = useCallback(() => {
    setIsCopyShoppingListPanelOpen(false);
    setCopyingShoppingList(null);
    setCopyFolderIds([]);
  }, []);

  const resolveTargetLists = useCallback(
    (shoppingList: ShoppingList | null | undefined, shoppingLists: ShoppingList[]) => {
      if (shoppingList) {
        return {
          primary: shoppingList,
          folderIds: toFolderIds([shoppingList.id]),
        };
      }

      const selectedLists = shoppingLists.filter((list) =>
        selectedShoppingListIds.includes(list.id)
      );
      return {
        primary: selectedLists[0] ?? null,
        folderIds: toFolderIds(selectedLists.map((list) => list.id)),
      };
    },
    [selectedShoppingListIds]
  );

  const openShareShoppingListPanel = useCallback(
    (shoppingList: ShoppingList | null = null) => {
      setIsCopyShoppingListPanelOpen(false);
      setCopyingShoppingList(null);
      setCopyFolderIds([]);

      const allLists = mapFavoriteFoldersToShoppingLists(favoriteFolders);
      const { primary, folderIds } = resolveTargetLists(shoppingList, allLists);
      setSharingShoppingList(primary);
      setShareFolderIds(folderIds);
      setIsShareShoppingListPanelOpen(true);
    },
    [favoriteFolders, resolveTargetLists]
  );

  const openCopyShoppingListPanel = useCallback(
    (shoppingList: ShoppingList | null = null) => {
      setIsShareShoppingListPanelOpen(false);
      setSharingShoppingList(null);
      setShareFolderIds([]);

      const allLists = mapFavoriteFoldersToShoppingLists(favoriteFolders);
      const { primary, folderIds } = resolveTargetLists(shoppingList, allLists);
      setCopyingShoppingList(primary);
      setCopyFolderIds(folderIds);
      setIsCopyShoppingListPanelOpen(true);
    },
    [favoriteFolders, resolveTargetLists]
  );

  const shareShoppingList = useCallback(
    async (request: ShareShoppingListRequest) => {
      const response = await shareShoppingListAsync(request);
      const folderNamesById = Object.fromEntries(
        favoriteFolders.map((folder) => [folder.id, folder.description])
      );
      const messages = buildShareShoppingListToastMessages(
        response.results ?? [],
        t,
        folderNamesById
      );
      if (messages.length > 0) {
        toast.success(messages.join('\n'), { style: { whiteSpace: 'pre-line' } });
      }
      setSelectedShoppingListIds([]);
      return response;
    },
    [favoriteFolders, shareShoppingListAsync, t]
  );

  const copyShoppingList = useCallback(
    async (request: CopyShoppingListRequest) => {
      const response = await copyShoppingListAsync(request);
      toast.success(
        t('List copied to {{count}} user(s).', {
          count: response,
        })
      );
      setSelectedShoppingListIds([]);
      return response;
    },
    [copyShoppingListAsync, t]
  );

  const exportSelectedShoppingLists = useCallback(async () => {
    const selectedLists = mapFavoriteFoldersToShoppingLists(favoriteFolders).filter((list) =>
      selectedShoppingListIds.includes(list.id)
    );

    if (selectedLists.length === 0) {
      return;
    }

    setIsExportingShoppingLists(true);
    try {
      const sections = await fetchShoppingListsForExport(
        selectedLists.map((list) => ({
          folderId: Number(list.id),
          listName: list.title,
        }))
      );
      await exportShoppingListsToExcel(sections, t);
    } catch {
      toast.error(t('Failed to export shopping list(s)'));
    } finally {
      setIsExportingShoppingLists(false);
    }
  }, [favoriteFolders, selectedShoppingListIds, t]);

  useEffect(() => {
    if (isError) {
      toast.error(t('Failed to load data'));
    }
  }, [isError, t]);

  const allShoppingLists = useMemo(
    () => mapFavoriteFoldersToShoppingLists(favoriteFolders),
    [favoriteFolders]
  );

  const shoppingListsDataState = useMemo(
    () => buildShoppingListsDataState(selectedFilter, selectedSort, clientId, debouncedSearch),
    [selectedFilter, selectedSort, clientId, debouncedSearch]
  );

  const shoppingLists = useMemo(
    () => process(allShoppingLists, shoppingListsDataState).data as ShoppingList[],
    [allShoppingLists, shoppingListsDataState]
  );

  const toggleAllShoppingListSelection = useCallback(() => {
    setSelectedShoppingListIds((prev) => {
      const visibleIds = shoppingLists.map((list) => list.id);
      if (visibleIds.length === 0) {
        return prev;
      }

      const allVisibleSelected = visibleIds.every((id) => prev.includes(id));
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleIds.includes(id));
      }

      return Array.from(new Set([...prev, ...visibleIds]));
    });
  }, [shoppingLists]);

  const hasActiveFilters =
    selectedFilter.value !== SHOPPING_LIST_FILTER_OPTIONS[0].value ||
    selectedSort.value !== SHOPPING_LIST_SORT_OPTIONS[0].value ||
    searchValue.trim().length > 0;
  const hasSelectedShoppingLists = selectedShoppingListIds.length > 0;
  const canExportSelectedShoppingLists = hasSelectedShoppingLists;

  const selectedFilterRef = useRef(selectedFilter);
  selectedFilterRef.current = selectedFilter;

  const handleSelectedFilterChange = useCallback((filter: ShoppingListFilterOption) => {
    if (filter.value !== selectedFilterRef.current.value) {
      setSelectedShoppingListIds([]);
    }
    setSelectedFilter(filter);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchValue('');
    if (selectedFilterRef.current.value !== SHOPPING_LIST_FILTER_OPTIONS[0].value) {
      setSelectedShoppingListIds([]);
    }
    setSelectedFilter(SHOPPING_LIST_FILTER_OPTIONS[0]);
    setSelectedSort(SHOPPING_LIST_SORT_OPTIONS[0]);
  }, []);

  useEffect(() => {
    const availableIds = new Set(shoppingLists.map((list) => list.id));
    setSelectedShoppingListIds((prev) => {
      const next = prev.filter((id) => availableIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [shoppingLists]);

  const contextValue = useMemo<ViewShoppingListsContextShape>(
    () => ({
      shoppingLists,
      isLoading,
      searchValue,
      setSearchValue,
      selectedFilter,
      handleSelectedFilterChange,
      selectedSort,
      setSelectedSort,
      hasActiveFilters,
      clearFilters,
      selectedShoppingListIds,
      hasSelectedShoppingLists,
      canExportSelectedShoppingLists,
      toggleShoppingListSelection,
      toggleAllShoppingListSelection,
      exportSelectedShoppingLists,
      isExportingShoppingLists,
      canShareShoppingLists,
      canCopyShoppingLists,
      glCodeOptions,
      areGLCodesLoading,
      isCreateShoppingListPanelOpen,
      openCreateShoppingListPanel,
      closeCreateShoppingListPanel,
      editingShoppingList,
      isEditShoppingListPanelOpen,
      openEditShoppingListPanel,
      closeEditShoppingListPanel,
      upsertShoppingList,
      deleteShoppingList,
      deleteSelectedShoppingLists,
      isUpsertingShoppingList,
      isDeletingShoppingList,
      deletingShoppingListId,
      sharingShoppingList,
      shareFolderIds,
      isShareShoppingListPanelOpen,
      openShareShoppingListPanel,
      closeShareShoppingListPanel,
      shareShoppingList,
      isSharingShoppingList,
      copyingShoppingList,
      copyFolderIds,
      isCopyShoppingListPanelOpen,
      openCopyShoppingListPanel,
      closeCopyShoppingListPanel,
      copyShoppingList,
      isCopyingShoppingList,
    }),
    [
      shoppingLists,
      isLoading,
      searchValue,
      selectedFilter,
      handleSelectedFilterChange,
      selectedSort,
      hasActiveFilters,
      clearFilters,
      selectedShoppingListIds,
      hasSelectedShoppingLists,
      canExportSelectedShoppingLists,
      toggleShoppingListSelection,
      toggleAllShoppingListSelection,
      exportSelectedShoppingLists,
      isExportingShoppingLists,
      canShareShoppingLists,
      canCopyShoppingLists,
      glCodeOptions,
      areGLCodesLoading,
      isCreateShoppingListPanelOpen,
      openCreateShoppingListPanel,
      closeCreateShoppingListPanel,
      editingShoppingList,
      isEditShoppingListPanelOpen,
      openEditShoppingListPanel,
      closeEditShoppingListPanel,
      upsertShoppingList,
      deleteShoppingList,
      deleteSelectedShoppingLists,
      isUpsertingShoppingList,
      isDeletingShoppingList,
      deletingShoppingListId,
      sharingShoppingList,
      shareFolderIds,
      isShareShoppingListPanelOpen,
      openShareShoppingListPanel,
      closeShareShoppingListPanel,
      shareShoppingList,
      isSharingShoppingList,
      copyingShoppingList,
      copyFolderIds,
      isCopyShoppingListPanelOpen,
      openCopyShoppingListPanel,
      closeCopyShoppingListPanel,
      copyShoppingList,
      isCopyingShoppingList,
    ]
  );

  return (
    <ViewShoppingListsContext.Provider value={contextValue}>
      {children}
    </ViewShoppingListsContext.Provider>
  );
};
